import React from 'react';

import { Box, DropdownMenu, IconButton, Link } from '@betterment/samba-react';
import { useNavigate } from 'react-router-dom';

import { HouseholdAccountsQuery } from './graphql/query.householdAccounts.generated';
import {
  accountCanAddSpouseToSolo,
  accountHasSolo401k,
  isSingleRothIRAAccount,
  isSingleTraditionalIRAAccount,
} from './utils';

import urlBuilder from '../../../Shared/url-builder';
import FormButton from '../../FormButton';
import { DropdownImpersonationLink } from '../DropdownImpersonationLink';

type EnvelopeAccount = HouseholdAccountsQuery['household']['accounts'][number];

export const AccountRowMenu: React.FC<{
  account: EnvelopeAccount;
  householdId: string;
  showImpersonationModel: boolean;
}> = ({ account, householdId, showImpersonationModel }) => {
  const navigate = useNavigate();
  const managedAccount = account.__typename === 'ManagedAccount' ? account : null;
  const { portfolio, legacyId: legacyGoalId } = managedAccount ?? {};
  const canEditPortfolio = portfolio && portfolio.isStrategySelectionEligible;
  const hasSolo401k = accountHasSolo401k(account);
  const canAddSpouseToSolo = accountCanAddSpouseToSolo(account);
  const isTraditionalIRA = isSingleTraditionalIRAAccount(managedAccount);
  const isRothIRA = isSingleRothIRAAccount(managedAccount);

  const { users } = account;

  return (
    <Box onClick={e => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenu.Trigger asChild={true}>
          <IconButton label="Show options" name="Ellipses" variant="solidSecondary" spacing="compact" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content align="end" side="bottom" css={{ minWidth: '200px' }}>
          <DropdownMenu.Item
            onSelect={() => {
              void navigate(urlBuilder.getHouseholdAccountPage.absolute({ householdId, accountId: account.id }));
            }}
          >
            Go to account
          </DropdownMenu.Item>
          {legacyGoalId && canEditPortfolio && (
            <DropdownMenu.Item>
              <Box css={{ width: 'size.full' }}>
                <FormButton
                  method="post"
                  action={urlBuilder.getEditPortfolioPage.absolute({
                    goal_id: legacyGoalId,
                    return_to_path: urlBuilder.getHouseholdSummaryPage.absolute({ householdId }),
                  })}
                  variant="textSecondary"
                  css={{ textDecoration: 'none', width: 'size.full' }}
                >
                  Edit portfolio
                </FormButton>
              </Box>
            </DropdownMenu.Item>
          )}
          {legacyGoalId && portfolio?.rebalance?.isRebalanceNowEligible && (
            <DropdownMenu.Item>
              <Link
                href={urlBuilder.getRebalanceNowPage.absolute({ goalId: legacyGoalId })}
                css={{ color: 'text', textDecoration: 'none', _hover: { textDecoration: 'none', color: 'text' } }}
              >
                Rebalance now
              </Link>
            </DropdownMenu.Item>
          )}
          {legacyGoalId && managedAccount && (
            <DropdownMenu.Item>
              <Link
                href={urlBuilder.getGoalRebalanceSettingsEditPage(legacyGoalId)}
                css={{ color: 'text', textDecoration: 'none', _hover: { textDecoration: 'none', color: 'text' } }}
              >
                Rebalance settings
              </Link>
            </DropdownMenu.Item>
          )}
          {isTraditionalIRA && managedAccount && (
            <DropdownMenu.Item key="convert to roth">
              <Link
                css={{ textDecoration: 'none' }}
                variant="textSecondary"
                href={urlBuilder.getHouseholdConvertFundsToRoth({
                  householdId,
                  subAccountId: managedAccount.subAccounts[0].legacySubAccountId,
                })}
              >
                Convert funds to roth
              </Link>
            </DropdownMenu.Item>
          )}
          {isRothIRA && managedAccount && (
            <DropdownImpersonationLink
              showModal={false}
              url={urlBuilder.getImpersonateClientPage.absolute({
                clientId: managedAccount.users[0].id,
                deep_link_path: `/ira_recharacterizations`,
              })}
              label="Recharacterize contributions"
              css={{ width: 'size.full' }}
            />
          )}
          {legacyGoalId && hasSolo401k && (
            <>
              {canAddSpouseToSolo && (
                <DropdownMenu.Item>
                  <Link
                    href={urlBuilder.getSolo401kAddSpouseParticipantPage.absolute({
                      householdId,
                      goalId: legacyGoalId,
                    })}
                    css={{ color: 'text', textDecoration: 'none', _hover: { textDecoration: 'none', color: 'text' } }}
                  >
                    Add spouse participant
                  </Link>
                </DropdownMenu.Item>
              )}
              <DropdownImpersonationLink
                showModal={showImpersonationModel}
                url={urlBuilder.getImpersonateClientPage.absolute({
                  clientId: users[0].id,
                  deep_link_path: '/app/b4b/documents',
                })}
                label="View plan documents"
                css={{ width: 'size.full' }}
              />
            </>
          )}
          <DropdownImpersonationLink
            showModal={showImpersonationModel}
            url={urlBuilder.getImpersonateClientPage.absolute({
              clientId: users[0].id,
              deep_link_path: '/app/deposit_setup/destination',
            })}
            label="Deposit"
            css={{ width: 'size.full' }}
          />
          <DropdownImpersonationLink
            showModal={showImpersonationModel}
            url={urlBuilder.getImpersonateClientPage.absolute({
              clientId: users[0].id,
              deep_link_path: '/app/withdrawal_setup',
            })}
            label="Withdrawal"
            css={{ width: 'size.full' }}
          />
        </DropdownMenu.Content>
      </DropdownMenu>
    </Box>
  );
};
